/* eslint-disable @next/next/no-img-element */
"use client";
import { useRef, useState, useMemo } from "react";
import { motion, AnimatePresence, useScroll, useSpring, useTransform } from "motion/react";
import { Code, Layout, Smartphone, Search, Gauge, Database, ShieldCheck } from "lucide-react";
import { useViewportHeight } from "@/app/hooks/useViewportHeight";
import { springConfig } from "@/app/hooks/useSpringConfig";
import Banner from "../Banner/Banner";

const BANNER_ITEMS = [
    { id: 1, icon: <Code size={120} />, label: "Entwicklung" },
    { id: 2, icon: <Layout size={120} />, label: "Webdesign" },
    { id: 3, icon: <Smartphone size={120} />, label: "Responsive" },
    { id: 4, icon: <Search size={120} />, label: "SEO" },
    { id: 5, icon: <Gauge size={120} />, label: "Performance" },
    { id: 6, icon: <Database size={120} />, label: "CMS" },
    { id: 7, icon: <ShieldCheck size={120} />, label: "Sicherheit" },
];

const TAB_SECTIONS = [
    {
        id: 'webdesign',
        emoji: '🎨',
        shortLabel: 'Webdesign',
        label: "Webdesign auf höchstem Niveau",
        text: "Maßgeschneiderte Websites für dein Business – Von WordPress & Webflow bis zu individuellen Lösungen. 🚀 Schnell & SEO-optimiert dank modernster Technologien. 🔧 Einfache Verwaltung mit intuitivem CMS. 🔗 Nahtlose Integration von CRM, E-Commerce & mehr. 🔒 Sicher & skalierbar für nachhaltiges Wachstum.",
        textShort: "Maßgeschneiderte Websites – schnell, SEO-optimiert und skalierbar.",
        image: "/img/leinwand.webp",
    },
    {
        id: 'web-apps',
        emoji: '⚡',
        shortLabel: 'Web-Apps',
        label: "Individuelle Webanwendungen",
        text: "Geht nicht? Gibt's nicht! 💥 Ich biete dir neben klassischen Webseiten 🌐 auch innovative Web-Anwendungen 💡, die deinen Kunden im Gedächtnis bleiben. Egal ob interaktive Elemente 🖱️, Animationen 🎥 oder komplexe Anwendungen 🛠️ – wir entwickeln etwas, das genau zu deinem Unternehmen passt!",
        textShort: "Innovative Web-Anwendungen mit interaktiven Elementen und Animationen.",
        image: "/img/office.webp",
    },
    {
        id: 'branding',
        emoji: '✨',
        shortLabel: 'Branding',
        label: "Deine Marke, authentisch im Web",
        text: "Eine erfolgreiche Webseite ist mehr als nur Technik – sie erzählt die Geschichte deines Unternehmens 📖 und lässt deine Marke leben 🌟. Ich helfe dir, deine Corporate Identity online erlebbar zu machen. Von der Farbwelt 🎨 über Schriftarten ✍️ bis zur Bildsprache 📸 – wir gestalten einen Webauftritt, der dein Unternehmen authentisch widerspiegelt!",
        textShort: "Corporate Identity online erlebbar machen – authentisch und professionell.",
        image: "/img/branding.webp",
    },
];

const SCROLL_KEYFRAMES = [0, 0.16, 0.6, 1];

const Leistungen = () => {
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const sectionRef = useRef<HTMLElement | null>(null);
    const viewportHeight = useViewportHeight();

    const selectedTab = useMemo(() => TAB_SECTIONS[selectedTabIndex], [selectedTabIndex]);

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"],
    });

    const liftY = useTransform(
        scrollYProgress,
        SCROLL_KEYFRAMES,
        [viewportHeight, 0, 0, -viewportHeight],
        { clamp: false },
    );

    const opacity = useTransform(scrollYProgress, [0, 0.2], [0, 1], { clamp: false });

    const smoothLiftY = useSpring(liftY, springConfig.default);

    const smoothOpacity = useSpring(opacity, springConfig.smooth);

    return (
        <section
            ref={sectionRef}
            className="bg-white relative w-full"
        >
            <span id="leistungen" className="block h-0 w-0" aria-hidden="true" />
            <motion.div
                initial={false}
                className="fixed inset-0 h-screen w-full flex flex-col items-start justify-start z-[60] shadow-[-6px_4px_0_#1d2b4f] bg-grid-pattern"
                style={{ 
                    y: smoothLiftY, 
                    opacity: smoothOpacity
                }}
            >
                <div className="pointer-events-auto w-full h-full flex flex-col items-center justify-between gap-2 lg:gap-6 overflow-visible">
                    <div
                        className="flex items-center justify-center flex-col w-full py-0 pt-20 lg:pt-30 leistungen-content-container h-full"
                        style={{ minHeight: `calc(90vh - 140px)` }}
                    >
                        <h2 className="text-3xl sm:text-5xl 2xl:text-6xl text-white stroke-text">Leistungen</h2>
                        <p className="text-sm lg:text-xl leading-tight text-black text-center font-medium max-w-[100%] md:max-w-[80%] lg:max-w-[800px] mb-2 lg:mb-6 px-4">
                            Von modernen Websites bis zu komplexen Webanwendungen – 
                            ich entwickle digitale Lösungen, die dein Business voranbringen.
                        </p>
                        
                        <div className="w-[97.5%] lg:w-[90%] 2xl:max-w-[50vw] flex flex-col overflow-hidden rounded-[12px] lg:rounded-[32px] border-[3px] lg:border-[4px] border-dashed border-[#1D2B4F] bg-white shadow-[6px_6px_0_rgba(29,43,79,0.2)] lg:shadow-[12px_12px_0_rgba(29,43,79,0.2)]">
                            <div className="flex border-b-[3px] lg:border-b-[4px] border-dashed border-[#1D2B4F]">
                                {TAB_SECTIONS.map((item, index) => (
                                    <motion.button
                                        key={item.id}
                                        onClick={() => setSelectedTabIndex(index)}
                                        whileHover={{ y: -2 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`flex-1 px-2 lg:px-6 py-2 lg:py-4 font-[Poppins] font-bold text-[9px] sm:text-[11px] lg:text-[20px] tab-text-size transition-all duration-200 border-r-[3px] lg:border-r-[4px] border-dashed border-[#1D2B4F] last:border-r-0 text-shadow-dark tracking-[0.5px] cursor-pointer text-white ${
                                            index === selectedTabIndex
                                                ? 'bg-[#5ba0ff]'
                                                : 'bg-white hover:bg-[#FFE066]'
                                        } ${
                                            index === 0 ? 'rounded-tl-[10px] lg:rounded-tl-[24px]' : index === 2 ? 'rounded-tr-[10px] lg:rounded-tr-[24px]' : ''
                                        }`}
                                    >
                                        {item.emoji} {item.shortLabel}
                                    </motion.button>
                                ))}
                            </div>

                            <div className="p-2 md:p-4 lg:p-4 lg:px-10 bg-white">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={selectedTab ? selectedTab.label : "empty"}
                                        initial={{ x: 20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={{ x: -20, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: "easeInOut" }}
                                        className="flex flex-col lg:flex-row items-center gap-4 lg:gap-10"
                                    >
                                        <div className="flex-1 flex flex-col justify-center">
                                            <h3 className="text-[20px] lg:text-[32px] font-bold text-[#1D2B4F]  font-luckiest leading-tight">
                                                {selectedTab.label}
                                            </h3>
                                            <p className="text-[13px] lg:text-[16px] font-[Poppins] font-medium leading-[20px] lg:leading-[26px] text-[#1D2B4F] mb-4 lg:mb-6 leistungen-text">
                                                <span className="short-text-show">{(selectedTab as any).textShort}</span>
                                                <span className="short-text-hide">{selectedTab.text}</span>
                                            </p>
                                            <div className="flex flex-col sm:flex-row gap-3 lg:gap-4 buttons-container">
                                                <motion.a
                                                    whileHover={{ y: -3, rotate: -1 }}
                                                    whileTap={{ scale: 0.96 }}
                                                    href="#kontakt"
                                                    className="btn-primary"
                                                >
                                                    Projekt starten →
                                                </motion.a>
                                                <motion.a
                                                    whileHover={{ y: -3, rotate: 1 }}
                                                    whileTap={{ scale: 0.96 }}
                                                    href="#arbeiten"
                                                    className="btn-secondary"
                                                >
                                                    Arbeiten ansehen
                                                </motion.a>
                                            </div>
                                        </div>
                                        <div className="flex flex-shrink-0 w-[140px] sm:w-[140px] lg:w-[280px] h-[100px] sm:h-[140px] lg:h-[280px] order-first lg:pt-6 leistungen-img-container">
                                            <img
                                                src={selectedTab.image}
                                                alt={`${selectedTab.shortLabel} Möwe`}
                                                className="w-full h-full object-contain"
                                                draggable="false"
                                            />
                                        </div>
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                    <Banner items={BANNER_ITEMS} speed={40000} />
                </div>
            </motion.div>
            <div className="h-[120vh]" aria-hidden="true" />
        </section>
    );
};

export default Leistungen;

